/**
 * etaProjection.js — Phase 5 Intelligence Layer.
 *
 * Projects weeks remaining to complete a job, with a confidence band
 * derived from throughput variance.
 *
 * Math:
 *   ETA_weeks = items_remaining / throughput (items/week)
 *
 *   Since throughput has variance, ETA does too — via ratio propagation
 *   (statistics.ratioBand). Low throughput → high ETA, so the bounds
 *   are FLIPPED when propagating.
 *
 *   Quality factor: if historical accuracy is < 100%, effective
 *   items_remaining is inflated by 100/accuracy% (clamped at
 *   PROJECTION_QUALITY_FACTOR_MAX to prevent ÷0 blow-up).
 *
 * Edge cases:
 *   - items_remaining ≤ 0 → deterministic(0) — already done
 *   - throughput.point ≤ 0 → insufficient_data (can't divide)
 *   - throughput.confidence === 'insufficient_data' → same on ETA
 *
 * Pure function.
 */

import { ratioBand, truncate } from '../statistics';
import { deterministic, insufficientData, fromStatsBand } from './confidenceBand';

// Minimum complexity factor — matches the migration's CHECK constraint that
// guards against data-entry errors. A weight < 0.1 = "1 item takes 1/10 the
// baseline time" which is unrealistic. Phase 11 slot 1 (Invariant #16).
const MIN_COMPLEXITY_FACTOR = 0.1;

/**
 * @param {number} itemsRemaining — non-negative count
 * @param {Projection} throughputProjection — items/week Projection
 * @param {number} [complexityFactor=1.0] — per-item complexity multiplier on
 *   items_remaining (e.g., 2.0 = items take 2× longer than baseline). Phase
 *   5.5 Step 5 sources this from `job.complexity_weight`. Previous name was
 *   `qualityFactor` (for rework inflation = 100/accuracy) which R3 H1 removed
 *   — rework is already handled by separate rework batches, so multiplying
 *   for rework double-counted. Complexity is the correct remaining use case
 *   for this multiplier.
 * @param {object} config — PROJECTION_* constants
 * @param {Date} [now] — R2 LOW-5: inject time for test stability / React identity
 * @returns {Projection} ETA in weeks
 */
export function projectETA(itemsRemaining, throughputProjection, complexityFactor, config, now = new Date()) {
  if (typeof itemsRemaining !== 'number' || !Number.isFinite(itemsRemaining)) {
    return insufficientData('weeks', 'items_remaining not numeric', now);
  }
  if (itemsRemaining <= 0) {
    return deterministic(0, 'weeks', now);
  }

  if (!throughputProjection || throughputProjection.confidence === 'insufficient_data') {
    return insufficientData('weeks', throughputProjection?.reason || 'no throughput data', now);
  }
  if (throughputProjection.point == null || throughputProjection.point <= 0) {
    // Phase 21.7 (Chapter 3 dogfood close 2026-04-30) UX-fix: this string
    // surfaces directly to users via DeadlineMetric. Pre-fix it read like
    // an internal warning ("zero or non-positive throughput") which is
    // confusing in customer-facing UI. Friendlier copy stays accurate
    // without the engineering jargon.
    return insufficientData('weeks', 'no recent annotation activity', now);
  }

  // Clamp complexity factor — same PROJECTION_QUALITY_FACTOR_MAX ceiling
  // (rename deferred to avoid an enums-wide touch; the value is the cap
  // for "how much can one item take relative to baseline"). Min 0.1
  // matches the migration's CHECK constraint — a weight < 0.1 is
  // treated as a data-entry error.
  const maxFactor = config.PROJECTION_QUALITY_FACTOR_MAX;
  const cf = Math.min(
    maxFactor,
    Math.max(MIN_COMPLEXITY_FACTOR, typeof complexityFactor === 'number' && Number.isFinite(complexityFactor) ? complexityFactor : 1.0),
  );

  const effectiveItems = itemsRemaining * cf;

  // ratioBand: numerator is deterministic (known item count); denominator
  // is throughput band. Low denom → high ratio, so bounds are flipped.
  const etaBand = ratioBand(effectiveItems, throughputProjection);

  if (etaBand.point == null) {
    return insufficientData('weeks', 'ratio computation failed', now);
  }

  const truncatedBand = truncate(etaBand, 0);

  return fromStatsBand({
    point: truncatedBand.point,
    low:   truncatedBand.low,
    high:  truncatedBand.high,
    method: throughputProjection.method,   // inherit method
    sampleSize: throughputProjection.sampleSize,
  }, throughputProjection.confidence, 'weeks', now);
}

/**
 * Convenience: projected completion date derived from ETA weeks + "now".
 * Returns { pointDate, lowDate, highDate } — all Date objects (or null).
 *
 * @param {Projection} etaProjection — from projectETA
 * @param {Date} [now=new Date()]
 */
export function projectedCompletionDate(etaProjection, now = new Date()) {
  const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;
  // R1 L7 + R2 MED-6 audit fix: accept both Date and numeric timestamps.
  // Numeric timestamps (e.g., Date.now() memoized via useMemo) are a
  // common React idiom; rejecting them used to fall back to `new Date()`
  // and introduce a silent millisecond-scale drift from the caller's
  // intended reference time. Now we accept both forms uniformly.
  let nowDate;
  if (now instanceof Date && !Number.isNaN(now.getTime())) {
    nowDate = now;
  } else if (typeof now === 'number' && Number.isFinite(now)) {
    nowDate = new Date(now);
  } else {
    nowDate = new Date();
  }
  if (!etaProjection || etaProjection.point == null) {
    return { pointDate: null, lowDate: null, highDate: null };
  }
  return {
    pointDate: new Date(nowDate.getTime() + etaProjection.point * MS_PER_WEEK),
    lowDate:  etaProjection.low  == null ? null : new Date(nowDate.getTime() + etaProjection.low  * MS_PER_WEEK),
    highDate: etaProjection.high == null ? null : new Date(nowDate.getTime() + etaProjection.high * MS_PER_WEEK),
  };
}
